import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { graphql } from 'gatsby'
import compact from 'lodash/compact'
import kebabCase from 'lodash/kebabCase'
import { Box } from 'reflexbox/styled-components'
import { useLocation } from '@reach/router'

import theme from 'theme'
import media from 'media'
import { H1 } from 'lib/ui/primitives'

import Layout from 'components/page/layouts/GatsbyLayout'
import UniversalLink from 'components/UniversalLink'
import Helmet from 'components/page/Helmet'
import PostGrid from 'components/blog/PostGrid'
import SidebarSection, {
  SidebarContainer,
  ChildrenContainer,
} from 'components/page/Section/SidebarSection'
import NewFormSummary from 'components/summaryCards/NewFormSummary'
import Section from 'components/page/Section'
import ContactAnvil from 'components/Signup/ContactAnvil'
import BlueSection from 'components/page/Section/BlueSection'
import Sidebar, {
  CLASS_NAME as CLASS_NAME_SIDEBAR,
} from 'components/page/Section/SidebarSection/FormSidebar'

import { FORMS_SLUG, TAG_ALL_ARTICLES } from 'consts'

import { getSocialCoverURL, parseQueryString, naturalSort } from 'helpers'
import renderNavbar from 'helpers/renderNavbar'

import formTags from 'lib/content/forms/tags'
import useHistoryState from 'hooks/useHistoryState'
import useSearch from 'hooks/useSearch'

import { SolutionsCTA } from './components'
import PDFTemplatesTestDrive from 'components/test-drive/PDFTemplatesTestDrive'

import seoData from './seo'

// TODO: CANDYCODE migrate to next.js

const tags = formTags.default
const FORM_SCROLL_NAME = 'contact-sales'

const StyledSolutionsCTA = styled(SolutionsCTA)`
  margin-top: 80px;
`

const Container = styled.div``

const TagContainer = styled.div``

const TagTitle = styled.div`
  margin-bottom: 20px;
  font-weight: ${theme.fontWeights.bold};
  font-size: ${theme.fontSizes[160]}px;
`

const StyledSidebarSection = styled(SidebarSection)`
  ${ChildrenContainer} {
    width: 100%;
    padding-left: 0;
  }

  ${SidebarContainer} {
    flex-direction: column;
  }

  ${TagContainer} {
    :first-child {
      margin-top: -12px;
    }

    :not(:first-child) ${TagTitle} {
      margin-top: 20px;
    }
  }

  ${media.giant`
   .${CLASS_NAME_SIDEBAR} {
      min-width: unset;
      max-width: 250px;
    }

   ${SidebarContainer} {
      flex-direction: row;
    }

    ${ChildrenContainer} {
      padding-left: 40px;
    }
  `}

  ${media.beyondGiant`
    ${ChildrenContainer} {
      padding-left: 50px;
    }
  `}
`

const TITLE_MAX_WIDTH = 550

const Title = styled(H1).attrs({
  maxWidth: [TITLE_MAX_WIDTH],
})`
  font-weight: ${theme.fontWeights.bold};
`

const TitleSubtitle = styled(Box).attrs({
  my: [20],
  maxWidth: [TITLE_MAX_WIDTH],
  textAlign: ['center'],
})`
  font-size: ${theme.fontSizes[60]}px;
`

const TitleCTA = styled(Box).attrs({
  maxWidth: [TITLE_MAX_WIDTH],
  textAlign: ['center'],
})`
  font-size: 15px;
`

const TagSection = React.memo(({ tag, tagItems, selectedTag }) => {
  const posts = tagItems.map(
    ({ title, tagline, workflowEid, slug, pdfScreenshot }) => ({
      title,
      description: tagline,
      isPdfTemplate: !workflowEid,
      pdfScreenshot,
      tag,
      to: `/${FORMS_SLUG}/${kebabCase(tag.toLowerCase())}/${slug}/`,
    })
  )

  return (
    <TagContainer>
      <TagTitle>{tag}</TagTitle>
      <PostGrid
        allPosts={posts}
        tag={tag}
        postComponent={NewFormSummary}
        columns={3}
        baseSlug={FORMS_SLUG}
        postProps={{
          state: { selectedTag },
          currentTag: tag,
        }}
      />
    </TagContainer>
  )
})

TagSection.propTypes = {
  tag: PropTypes.string.isRequired,
  tagItems: PropTypes.array.isRequired,
}

function LibraryIndex ({
  location: { pathname, state: locationState, search },
  data: {
    allMarkdownRemark: { nodes },
    localSearchLibrary,
    allPDFScreenshotPNG,
  },
  // NOTE: this tags page context is from gatsby-node.js, and it was set up that way bc of how the blog
  // index page was set up. It likely will be refactored when we redesign the blog.
  pageContext: { tags: initialTags, pageNumber },
}) {
  const INITIAL_SELECTED_TAG = initialTags?.[0].toLowerCase()
  const posts = nodes
    .map((post) => ({
      ...post.frontmatter,
      pdfScreenshot: post?.pdfScreenshot,
    }))
    .sort((a, b) => naturalSort(a.title, b.title))
  const { onClearSearch, onSearch, query, results, renderNoResults } =
    useSearch({
      pageSlug: FORMS_SLUG,
      tag: INITIAL_SELECTED_TAG,
      pageNumber,
      posts,
    })

  const [selected, setSelected] = useHistoryState(
    'formSelectedTag',
    INITIAL_SELECTED_TAG || TAG_ALL_ARTICLES.toLowerCase()
  )
  const [templateType, setTemplateType] = useState(null)

  const isAllArticlesSelected = selected === TAG_ALL_ARTICLES.toLowerCase()

  useEffect(() => {
    // TODO: prob should write someting over localStorage//UniversalLink to ensure
    // no collisions with keys
    const incomingSelectedTag =
      locationState?.selected || localStorage.getItem('selectedFormTag')

    if (incomingSelectedTag) {
      setSelected(incomingSelectedTag.toLowerCase())

      localStorage.removeItem('selectedFormTag')
    }

    const queryParams = parseQueryString(search)
    if (
      queryParams?.workflows !== undefined &&
      queryParams?.workflows !== null
    ) {
      setTemplateType('workflows')
    } else if (
      queryParams?.pdfTemplates !== undefined &&
      queryParams?.pdfTemplates !== null
    ) {
      setTemplateType('pdfTemplates')
    } else {
      setTemplateType(null)
    }
  }, [])

  function renderTitle () {
    return (
      <BlueSection renderAs="SubSection" size="small">
        <Title>Form library</Title>
        <TitleSubtitle>
          Anvil's form templates make managing documents easier than ever.
          Explore our categories to fast-track your development.
        </TitleSubtitle>
        <TitleCTA>
          Have a unique need?{' '}
          <UniversalLink to={FORM_SCROLL_NAME}>Book a demo</UniversalLink> to
          discuss it with our team.
        </TitleCTA>
      </BlueSection>
    )
  }

  function renderSidebarSection () {
    const onItemClick = (tag) => {
      onClearSearch()

      setSelected(
        tag.toLowerCase(),
        TAG_ALL_ARTICLES.toLowerCase() === tag.toLowerCase()
          ? `/${FORMS_SLUG}/`
          : `/${FORMS_SLUG}/${kebabCase(tag.toLowerCase())}/`
      )
    }

    const menuItems = compact(
      tags.map((tag) => {
        return nodes.filter((node) => node.frontmatter.tags.includes(tag))
          .length > 0
          ? {
              key: tag.toLowerCase(),
              value: tag,
              onClick: () => onItemClick(tag),
            }
          : null
      })
    )

    menuItems.unshift({
      key: TAG_ALL_ARTICLES.toLowerCase(),
      value: TAG_ALL_ARTICLES,
      onClick: () => onItemClick(TAG_ALL_ARTICLES.toLowerCase()),
    })

    const tagSections = tags.map((tag) => {
      if (
        selected !== TAG_ALL_ARTICLES.toLowerCase() &&
        selected !== tag.toLowerCase()
      )
        return null

      const tagItems = results?.filter((result) => {
        const isPdfTemplate = !result?.workflowEid
        if (templateType === 'workflows') {
          return result?.tags.includes(tag) && !isPdfTemplate
        } else if (templateType === 'pdfTemplates') {
          return result?.tags.includes(tag) && isPdfTemplate
        }

        return result?.tags.includes(tag)
      })

      return tagItems.length > 0 ? (
        <TagSection
          key={tag.toLowerCase()}
          tag={tag}
          tagItems={tagItems}
          selectedTag={selected}
        />
      ) : null
    })

    const selectedValue = menuItems.find(
      (menuItem) => menuItem.key === selected
    )?.value

    const thereAreForms = !tagSections.every((section) => section === null)

    return (
      <StyledSidebarSection
        renderAs="SubSection"
        title="Form Library"
        description="Fill out forms yourself, copy Workflows into your Anvil account, or fork production ready PDF templates for use with API."
        sidebarComponent={Sidebar}
        menuItems={menuItems}
        selectedItem={selectedValue}
        isFirstSection
        search={{
          localSearchData: localSearchLibrary,
          placeholder: 'Search library',
          results,
          onSearch,
          query,
        }}
        onChange={onItemClick}
      >
        {thereAreForms
          ? tagSections
          : renderNoResults({
              message: (
                <>
                  We can't find anything matching <br /> your search terms.
                </>
              ),
              linkText: 'Browse all',
            })}
        {!isAllArticlesSelected && thereAreForms && (
          <StyledSolutionsCTA
            tag={selectedValue}
            renderTitle={(tag) =>
              `Anvil has more solutions for ${tag} companies`
            }
          />
        )}
      </StyledSidebarSection>
    )
  }

  const renderSignup = () => <ContactAnvil scrollName={FORM_SCROLL_NAME} />

  const { pathname: pagePath } = useLocation()
  const { title, description, keywords, socialTagline } =
    seoData[pagePath] || {}

  const getSocialTag = (tag) => {
    if (tag.toLowerCase() === TAG_ALL_ARTICLES.toLowerCase()) return 'index'

    return kebabCase(tag.toLowerCase())
  }

  return (
    <Layout themeId="formLibrary">
      <Container>
        <Helmet
          title={title}
          meta={{ description, keywords, socialTagline }}
          pathname={pathname}
          socialImageURL={getSocialCoverURL('library', getSocialTag(selected))}
        />
        {renderNavbar()}
        <Section style={{ overflow: 'unset' }}>
          {renderTitle()}
          {renderSidebarSection()}
        </Section>
        <PDFTemplatesTestDrive title="Can’t find a form you’re looking for? Templatize your PDF in seconds." />
        {renderSignup()}
      </Container>
    </Layout>
  )
}

export const pageQuery = graphql`
  query FormsIndexQuery {
    allMarkdownRemark(
      filter: { frontmatter: { type: { in: ["Form", "Pack"] } } }
    ) {
      nodes {
        excerpt(pruneLength: 250)
        frontmatter {
          slug
          title
          tags
          tagline
          type
          title
          pdfTemplateEid
          workflowEid
        }
        pdfScreenshot {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }
    localSearchLibrary {
      index
      store
    }
  }
`

LibraryIndex.defaultProps = {
  pageContext: {
    pageNumber: 0,
  },
}

LibraryIndex.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.shape({
    tags: PropTypes.arrayOf(PropTypes.string),
    pageNumber: PropTypes.number,
    pagePath: PropTypes.string,
  }),
}

export default LibraryIndex
